import { Injectable, signal } from '@angular/core';

export interface Toast {
  id: string;
  message: string;
  type: 'success' | 'danger' | 'info' | 'warning' | 'invite';
  duration?: number;
  imageUrl?: string;
  onAccept?: () => void;
  onDecline?: () => void;
}

@Injectable({
  providedIn: 'root',
})
export class ToasterService {
  public toasts = signal<Toast[]>([]);

  public show(
    message: string,
    type: 'success' | 'danger' | 'info' | 'warning' | 'invite' = 'info',
    duration: number = 4000,
    onAccept?: () => void,
    onDecline?: () => void,
    imageUrl?: string
  ): string {
    const id = Math.random().toString(36).substring(2, 9);
    const toast: Toast = { id, message, type, duration, onAccept, onDecline, imageUrl };

    this.toasts.update(current => [...current, toast]);

    if (duration > 0) {
      setTimeout(() => {
        this.dismiss(id);
      }, duration);
    }
    return id;
  }

  public success(message: string, duration?: number) {
    this.show(message, 'success', duration);
  }

  public danger(message: string, duration?: number) {
    this.show(message, 'danger', duration);
  }

  public info(message: string, duration?: number) {
    this.show(message, 'info', duration);
  }

  public warning(message: string, duration?: number) {
    this.show(message, 'warning', duration);
  }

  public invite(message: string, onAccept: () => void, onDecline?: () => void): string {
    return this.show(message, 'invite', 0, onAccept, onDecline);
  }

  public desktopNotification(title: string, body: string, iconUrl: string = '/images/logo_1_4.png', onClick?: () => void) {
    if (!("Notification" in window)) {
      return;
    }

    const createNotification = () => {
      const notification = new Notification(title, { body, icon: iconUrl });
      notification.onclick = (e) => {
        e.preventDefault();
        window.focus(); // Standard browser fallback

        // If running in Electron, explicitly restore the window via the exposed API
        const winControls = (window as any).windowControls;
        if (winControls) {
          winControls.restore();
        }

        if (onClick) {
          onClick();
        }
        notification.close();
      };
    };

    if (Notification.permission === "granted") {
      createNotification();
    } else if (Notification.permission !== "denied") {
      Notification.requestPermission().then(permission => {
        if (permission === "granted") {
          createNotification();
        }
      });
    }
  }

  public dismiss(id: string) {
    this.toasts.update(current => current.filter(t => t.id !== id));
  }
}
